import {
  BitkitActionMenu,
  BitkitAlert,
  BitkitBadge,
  BitkitButton,
  BitkitControlButton,
  IconDoc,
  IconHistory,
  IconMoreHorizontal,
} from '@bitrise/bitkit-v2';
import { useEffect, useRef, useState } from 'react';

import { getCiConfigExpertConversation } from '@/client/BFF/AIConversation/useAIConversation';
import useAIConversationTurnPayload from '@/client/BFF/AIConversation/useAIConversationTurnPayload';
import { AIHistoryItem, AILoadingText, AIMessage } from '@/components/AIChat/AIChat';
import AIDrawerEmptyState from '@/components/CIConfigExpert/AIDrawerEmptyState';
import ConfirmationDialog from '@/components/Dialog/ConfirmationDialog/ConfirmationDialog';
import { HEADER_HEIGHT } from '@/constants';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { useMutateUserMetadata } from '@/hooks/api/useUserMetadata';
import { SimpleErrorResponse } from '@/models/Error';
import postMessageToWorkflowEditor from '@/pages/WorkflowEditorPage/utils/postMessageToWorkflowEditor';
import useWorkflowEditorPageStore from '@/pages/WorkflowEditorPage/WorkflowEditorPage.store';
import { abortBuildPath } from '@/utils/monolithApiRouteService';
import { queryClient } from '@/utils/query';

import AIDrawer from '../AIDrawer/AIDrawer';
import { useOverlaySidebar } from '../page/PlatformShell/OverlaySidebar';
import {
  trackChangesApplied,
  trackChatCleared,
  trackPromptingGuideOpened,
  trackPromptSubmitted,
  trackResponseRated,
  trackResponseReceived,
  trackSessionDismissed,
} from './ciConfigExpertTracking';
import useCIConfigExpertConversation, { CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY } from './useCIConfigExpertConversation';

export type CIConfigExpertActionTypes =
  'create_pipeline' | 'create_workflow' | 'explain_pipeline' | 'explain_workflow' | 'general_ask_ai';

export type CIConfigExpertAction = {
  action?: CIConfigExpertActionTypes;
  bitriseYmlContents: string;
  selectedPage: string;
  yamlSelector?: string;
};

type CIConfigExpertProps = CIConfigExpertAction & {
  ciConfigSlug: string;
  onApply: (ciConfig: string, turnIndex: number, turnCount: number) => void;
  onHide: () => void;
  selectedPage: string;
};

export const useCiConfigExpert = () => {
  const { hide, show, visibility } = useOverlaySidebar();
  return { hide, show, visibility };
};

const CIConfigExpert = (props: CIConfigExpertProps) => {
  const { action, bitriseYmlContents, ciConfigSlug, yamlSelector, onApply, onHide, selectedPage } = props;

  const conversationId = useWorkflowEditorPageStore((s) => s.conversationId);
  const { visibility } = useOverlaySidebar();

  const [message, setMessage] = useState('');
  const [applyTurnId, setApplyTurnId] = useState<string | null>(null);

  const { history, inProgress, isLoading, refetch } = useCIConfigExpertConversation();
  const { mutate: saveLastViewedTurn } = useMutateUserMetadata();

  useEffect(() => {
    if (visibility === 'hidden' || visibility === 'closed') {
      useWorkflowEditorPageStore.setState({ hasBeenOpened: false });
    }
  }, [visibility]);

  const sessionStartRef = useRef<number>(0);
  useEffect(() => {
    sessionStartRef.current = Date.now();
  }, []);

  const prevHistoryLengthRef = useRef(history.length);
  useEffect(() => {
    if (history.length > prevHistoryLengthRef.current) {
      const lastTurn = history[history.length - 1];
      if (lastTurn.status === 'successful') {
        trackResponseReceived({
          appSlug: ciConfigSlug,
          conversationId,
          action,
          turnIndex: lastTurn.turnSequence,
          responseTimeMs: lastTurn.turnDurationSeconds * 1000,
          hasResponsePayload: lastTurn.hasResponsePayload,
        });
      }
    }
    prevHistoryLengthRef.current = history.length;
  }, [action, ciConfigSlug, conversationId, history]);

  const lastHistoryTurnId = history[history.length - 1]?.turnId;

  const isPanelVisible = visibility === 'visible' || visibility === 'immediate';
  useEffect(() => {
    if (lastHistoryTurnId && isPanelVisible) {
      const value = `${ciConfigSlug}#${lastHistoryTurnId}`;
      saveLastViewedTurn({ ci_config_expert_last_viewed_turn: value });
      queryClient.setQueryData(['usermetadata', CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY], { value });
    }
  }, [lastHistoryTurnId, ciConfigSlug, saveLastViewedTurn, isPanelVisible]);

  const [isNotificationDismissed, setIsNotificationDismissed] = useState(false);
  const isNotificationVisible = !!inProgress && !isNotificationDismissed && action?.startsWith('create');

  const abortTurnRequest = useMonolithApiMutation<unknown, unknown, void>(
    abortBuildPath(inProgress?.turnId || ''),
    'post',
  );

  const { isFetching: isFetchingPayload, refetch: fetchPayload } = useAIConversationTurnPayload(
    { ciConfigSlug, turnId: applyTurnId ?? '' },
    { enabled: false },
  );

  const handleApplyConfirm = async () => {
    const result = await fetchPayload();
    if (result.data) {
      const applyTurn = history.find((t) => t.turnId === applyTurnId);
      onApply(result.data, applyTurn?.turnSequence ?? history.length, history.length);
      setApplyTurnId(null);
      trackChangesApplied({
        appSlug: ciConfigSlug,
        conversationId,
        action,
        turnCount: history.length,
      });
    }
  };

  const turnMutation = useMonolithApiMutation<
    { buildSlug: string; conversationId: string },
    SimpleErrorResponse,
    {
      aiRunType: 'ci-config-expert';
      userMessage: string;
      context: {
        bitriseYmlContents: string;
        selectedPage: string;
        yamlSelector?: string;
      };
    }
  >(`/apps/${ciConfigSlug}/agent_runs`, 'POST', {
    onSuccess: (data) => {
      useWorkflowEditorPageStore.setState({ conversationId: data.data.conversationId });
      refetch();
    },
  });

  const sendMessage = () => {
    trackPromptSubmitted({
      appSlug: ciConfigSlug,
      conversationId,
      action,
      turnIndex: history.length,
      isFirstPrompt: history.length === 0,
      promptCharCount: message.length,
    });
    turnMutation.mutate({
      aiRunType: 'ci-config-expert',
      userMessage: message,
      context: { bitriseYmlContents, selectedPage, yamlSelector },
    });
    setMessage('');
    setIsNotificationDismissed(false);
  };

  const hasSentAutoMessageRef = useRef(false);

  useEffect(() => {
    hasSentAutoMessageRef.current = false;
  }, [action, yamlSelector]);

  useEffect(() => {
    if (hasSentAutoMessageRef.current) return;
    if (action !== 'explain_workflow' && action !== 'explain_pipeline') return;
    if (!yamlSelector) return;

    hasSentAutoMessageRef.current = true;
    const entity = action === 'explain_workflow' ? 'workflow' : 'pipeline';
    const parts = yamlSelector.split('.');
    const name = parts[parts.length - 1];

    turnMutation.mutate({
      aiRunType: 'ci-config-expert',
      userMessage: `Explain '${name}' ${entity}`,
      context: { bitriseYmlContents, selectedPage, yamlSelector },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, bitriseYmlContents, selectedPage, yamlSelector]);

  const handleStop = () => {
    if (!inProgress) return;

    abortTurnRequest.mutate(undefined, {
      async onSuccess() {
        await refetch();
        setMessage(inProgress.userMessage);
      },
    });
  };

  const clearConversationMutation = useMonolithApiMutation<unknown, unknown, void>(
    getCiConfigExpertConversation({ ciConfigSlug }),
    'DELETE',
    {
      onSuccess: () => {
        refetch();
        trackChatCleared({
          appSlug: ciConfigSlug,
          conversationId,
          action,
          turnCount: history.length,
        });
      },
    },
  );

  const handleClearChat = () => {
    clearConversationMutation.mutate(undefined);
  };

  const handleHide = () => {
    postMessageToWorkflowEditor('CI_CONFIG_EXPERT_CLOSED');
    onHide();
    trackSessionDismissed({
      appSlug: ciConfigSlug,
      conversationId,
      action,
      turnCount: history.length,
      sessionDurationS: Math.round((Date.now() - sessionStartRef.current) / 1000),
    });
  };

  const turnError = turnMutation.error?.response?.data;
  const errorMessage = turnError
    ? (turnError.errorMsg ?? turnError.errorMessage ?? turnError.error ?? turnError.message)
    : undefined;

  const isInputDisabled = Boolean(inProgress) || abortTurnRequest.isPending || turnMutation.isPending;

  useEffect(() => {
    if (inProgress) {
      postMessageToWorkflowEditor('DISABLE_AI_BUTTONS');
    } else {
      postMessageToWorkflowEditor('ENABLE_AI_BUTTONS');
    }
    return () => {
      postMessageToWorkflowEditor('ENABLE_AI_BUTTONS');
    };
  }, [inProgress]);

  return (
    <>
      <ConfirmationDialog
        isVisible={!!applyTurnId}
        title="Apply changes?"
        text="This will replace what's in the editor, including any manual edits you made while the config was being generated."
        confirmText="Apply changes"
        closeText="Cancel"
        isDanger={false}
        isInProgress={isFetchingPayload}
        onConfirm={handleApplyConfirm}
        onClose={() => !isFetchingPayload && setApplyTurnId(null)}
      />
      <AIDrawer
        errorMessage={errorMessage}
        onErrorDismiss={turnMutation.reset}
        isInputDisabled={isInputDisabled}
        isLoading={turnMutation.isPending}
        isStopping={abortTurnRequest.isPending}
        message={message}
        moreActions={
          <BitkitActionMenu trigger={<BitkitControlButton icon={IconMoreHorizontal} size="sm" label="More" />}>
            {(history.length > 0 || !!inProgress) && (
              <BitkitActionMenu.Item value="clear-chat" icon={IconHistory} onClick={handleClearChat}>
                Clear chat
              </BitkitActionMenu.Item>
            )}
            <BitkitActionMenu.Item
              value="get-help-on-prompting"
              href="https://docs.bitrise.io/en/bitrise-ci/workflows-and-pipelines/ai-configuration-assistant.html"
              isExternal
              icon={IconDoc}
              onClick={() =>
                trackPromptingGuideOpened({
                  appSlug: ciConfigSlug,
                  conversationId,
                  action,
                  turnCount: history.length,
                })
              }
            >
              Get help on prompting
            </BitkitActionMenu.Item>
          </BitkitActionMenu>
        }
        onHide={handleHide}
        onMessageChange={setMessage}
        onSend={sendMessage}
        onStop={inProgress ? handleStop : undefined}
        title="AI Assistant"
        titleBadge={
          <BitkitBadge variant="subtle" colorVariant="orange">
            Beta
          </BitkitBadge>
        }
        top={HEADER_HEIGHT + 65}
      >
        {!isLoading && !history?.length && !inProgress && <AIDrawerEmptyState action={action} />}
        {history?.map((msg) => (
          <AIMessage userMessage={msg.userMessage} key={msg.turnSequence}>
            <AIHistoryItem
              status={msg.status}
              durationSeconds={msg.status === 'successful' ? msg.turnDurationSeconds : undefined}
              response={msg.status === 'successful' ? msg.assistantResponse : undefined}
              onFeedback={(reactionType) => {
                trackResponseRated({
                  appSlug: ciConfigSlug,
                  conversationId,
                  reactionType,
                  turnIndex: msg.turnSequence,
                  hasResponsePayload: msg.status === 'successful' ? msg.hasResponsePayload : undefined,
                });
              }}
            >
              {msg.status === 'successful' && msg.hasResponsePayload && (
                <BitkitButton
                  size="sm"
                  variant="ai-secondary"
                  onClick={() => setApplyTurnId(msg.turnId)}
                  width="fit-content"
                >
                  Apply changes
                </BitkitButton>
              )}
            </AIHistoryItem>
          </AIMessage>
        ))}
        {inProgress && (
          <AIMessage userMessage={inProgress.userMessage}>
            {isNotificationVisible && (
              <BitkitAlert
                variant="info"
                marginBlockEnd="16"
                dismissible
                onClose={() => setIsNotificationDismissed(true)}
                messageText="Edits you make now will be overwritten when you apply the Assistant's changes."
              />
            )}
            <AILoadingText />
          </AIMessage>
        )}
      </AIDrawer>
    </>
  );
};

export default CIConfigExpert;
